import React, { useState } from "react";
import { MapPin, Phone, Mail, Globe, Send, Building2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { organizer, secretariat, conference } from "../mock";
import { useReveal } from "../hooks/useReveal";
import { useToast } from "../hooks/use-toast";

const API_URL = "http://localhost:5001/api/contact";

const Contact = () => {
  useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Incomplete form", description: "Please fill name, email, and message." });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      toast({ title: "Message sent!", description: "Our team will get back to you shortly." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast({ title: "Failed to send", description: err.message || "Please try again later." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHeader title="Contact Us" crumb="Contact" subtitle="Reach out to the DRS 2026 secretariat and organising team." />
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div className="reveal rounded-3xl gradient-navy p-8 md:p-10 text-white">
            <div className="flex items-center gap-3 mb-6"><Building2 className="w-6 h-6 text-brand" /><h3 className="font-display font-bold text-xl">{organizer.org}</h3></div>
            <div className="space-y-5">
              <div className="flex items-start gap-4"><MapPin className="w-5 h-5 text-brand mt-0.5 shrink-0" /><span className="text-slate-300">{organizer.address}</span></div>
              {organizer.phones.map((p) => (
                <div key={p} className="flex items-center gap-4">
                  <Phone className="w-5 h-5 text-brand shrink-0" />
                  <a href={`tel:${p}`} className="text-slate-300 hover:text-white transition-colors">{p}</a>
                </div>
              ))}
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-brand shrink-0" />
                <a href={`mailto:${organizer.email}`} className="text-slate-300 hover:text-white transition-colors">{organizer.email}</a>
              </div>
              <div className="flex items-center gap-4">
                <Globe className="w-5 h-5 text-brand shrink-0" />
                <a href={`https://${organizer.website}`} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white transition-colors">{organizer.website}</a>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-white/15">
              <p className="text-sm text-slate-400">Conference Secretariat</p>
              <p className="font-display font-bold mt-1">{secretariat.org}</p>
              <p className="text-sm text-slate-300 mt-1">Organised by {conference.organisedBy}, endorsed by the {conference.endorsedBy}.</p>
            </div>
          </div>

          <form onSubmit={submit} className="reveal rounded-3xl border border-slate-100 shadow-sm p-8 md:p-10 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Your name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand" placeholder="you@example.com" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand" placeholder="+91" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject</label>
                <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Subject" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Message *</label>
              <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand resize-none" placeholder="How can we help?" />
            </div>
            <button type="submit" disabled={sending} className="btn-shine w-full h-12 rounded-full bg-brand hover:bg-brand-600 text-white font-semibold inline-flex items-center justify-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
